/**
 * @fileoverview WEB-651 / DPL-01 — how `/discover` consumes the app-chrome
 * geometry contract.
 *
 * ## The handoff's token names do not exist
 *
 * The Discovery v2 handoff describes DPL-01 as a drop-in swap to a `nav-v2/*`
 * package publishing `--gn-dock-h` and an `.o-gnav-stick-below` utility. That
 * package is a design artifact: Nav v2 shipped **natively** on the authed app
 * surface (WEB-634 / WEB-637 / WEB-638 / WEB-639), and WEB-647 published the
 * geometry under names aligned with the existing `--nav-*` family. Anyone
 * reading the handoff and grepping for `--gn-*` will find nothing, so the
 * mapping is recorded here — at the point of consumption — as well as in
 * `docs/design-system/discovery-v2-rulings.md` and
 * `components/organisms/AppShell/chromeGeometry.ts`.
 *
 * | Handoff name          | Real name shipped  | Published by                              |
 * | --------------------- | ------------------ | ----------------------------------------- |
 * | `--gn-dock-h`         | `--nav-dock-h`     | `AppShell/BottomNav.tsx` (measured)       |
 * | _(none — implied)_    | `--nav-stick-top`  | `AppShell/AppShellLayout.tsx`             |
 * | `.o-gnav-stick-below` | `.nav-stick-below` | `apps/web/styles/surfaces/app-chrome.css` |
 *
 * ## Why the class strings live in one module
 *
 * Same reason as {@link ./shellGrid.ts}: four surfaces render this shell — the
 * template, the route skeleton (`discover/loading.tsx`), the story, and the
 * results-bar organism — and a copy-pasted offset drifts silently. These are
 * plain string literals on purpose; Tailwind's JIT scans source text, so a
 * computed or interpolated class name compiles to nothing. For the same reason
 * this module lives under `components/` rather than `lib/` — `lib/**` is not in
 * `tailwind.config.mjs`'s `content` globs, so arbitrary-value classes declared
 * there would never be emitted.
 *
 * ## What this module does NOT do
 *
 * It restacks nothing. `.nav-stick-below` rides `--z-surface-sticky` (5,
 * WEB-1110), which leaves the WEB-611 / WEB-637 ladder authoritative and keeps
 * every Discover element below the chrome (60) and its popovers, the dock (90)
 * and the Locus FAB (95). Do not add a local z-index here.
 *
 * @since WEB-651 (Discovery v2 — DPL-01)
 */

import { DOCK_HEIGHT_VAR, STICK_TOP_VAR } from '@/components/organisms/AppShell/chromeGeometry';

/**
 * Re-exported so a reader of the Discover surface can follow the contract back
 * to its publisher without knowing the AppShell module by name.
 */
export { DOCK_HEIGHT_VAR, STICK_TOP_VAR };

/**
 * The results bar sticks directly under the sticky top chrome.
 *
 * `.nav-stick-below` (styles/surfaces/app-chrome.css) is the real `.o-gnav-stick-below`: it sets
 * `position: sticky`, `top: var(--nav-stick-top)`, and `z-index: var(--z-surface-sticky)`.
 * Consuming the utility rather than hand-rolling `sticky top-[82px]` is the
 * whole point — the offset tracks `--chrome-height` and cannot freeze a guess.
 *
 * `bg-background` is not decoration: a sticky bar with a transparent background
 * lets the list scroll through it and become unreadable.
 *
 * WEB-1081 (FRE-1377): the bar is sticky under the nav at EVERY width. The
 * retired ≤720 floor treatment (WEB-668 / WEB-768 / WEB-796 — `position:
 * fixed` above the dock) jumped with the mobile browser toolbar and stacked on
 * the app dock; there is no width-scoped offset for this bar any more.
 */
export const DISCOVER_STICK_BELOW_CHROME_CLASS = 'nav-stick-below bg-background';

/*
 * ## Where the dock clearance went (WEB-727)
 *
 * WEB-651 gave the results pane its own `pb-[var(--nav-dock-h)]`
 * (`DISCOVER_DOCK_CLEARANCE_CLASS`) because `AppShellLayout`'s `pb-28` was a
 * frozen 112px guess this surface could not lean on. The two never replaced
 * each other — they **stacked**: 112px of shell plus 77px of pane at 390×844,
 * ~189px of dead space between the last row and the footer, against ~112px on
 * Feed / Saved / Profile.
 *
 * WEB-727 fixed the root cause by making the shell's reservation derive from
 * the same measured token (`AppShell/chromeGeometry.ts` →
 * `SHELL_BOTTOM_RESERVATION_CLASS`), which leaves the pane's copy pure
 * duplication. Exactly one element reserves the band, and it is `<main>`. Do
 * not re-add a pane-local dock clearance here.
 */

/**
 * The desktop filter rail sticks under the chrome with a `1rem` breathing gap
 * and owns its own vertical scroll window (WEB-767).
 *
 * Before WEB-651 this was `min-[980px]:top-4` — a bare 16px tuned when the
 * surface had no sticky chrome above it, which parked the top of the rail
 * *behind* the 82px chrome band once WEB-634 landed. The `1rem` addend is a
 * content gap and stays a literal; the chrome part is the token.
 *
 * `.nav-stick-below` is deliberately not reused here: the rail needs that gap
 * and keeps its own stacking (a rail that outranks its own popovers is a new
 * bug), so it consumes `--nav-stick-top` directly.
 *
 * WEB-767: a sticky rail without a viewport-bounded height grows with its
 * filters and clips at the fold. The only way to see the clipped controls was
 * to scroll the List, which moved both regions at once. `max-height` is the
 * remaining viewport under the stuck `top` (`100dvh − --nav-stick-top − 1rem`)
 * — the no-JS ceiling once sticky engages. `DiscoverRailScrollport` tightens
 * that to the live remaining viewport so the scrollport is fully on screen
 * *before* sticky engages (hero still above the grid). `overflow-y: auto`
 * makes that box the scrollport; `overscroll-y-contain` keeps wheel / touch
 * gestures from chaining into the List while the pointer is inside this
 * region. Apply this class to the Sidebar **layout** region
 * (`<aside data-testid="discover-rail">`), not the inner filter stack — the
 * left gutter is part of the region the pointer isolation covers.
 */
export const DISCOVER_RAIL_STICKY_CLASS = [
  'min-[981px]:sticky',
  'min-[981px]:top-[calc(var(--nav-stick-top)_+_1rem)]',
  'min-[981px]:self-start',
  'min-[981px]:max-h-[calc(100dvh_-_var(--nav-stick-top)_-_1rem)]',
  'min-[981px]:overflow-y-auto',
  'min-[981px]:overscroll-y-contain',
].join(' ');
